import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdDoneAll, MdSave, MdSearch } from "react-icons/md";
import {
  API_BASE_URL,
  getAuthHeaders,
  getJsonAuthHeaders,
} from "../../../utils/api";
import { isConfiguredCampusClass } from "../../../utils/campusClasses";
import { getStoredCampusSections } from "../../../utils/campusSections";
import { C, Card, Badge } from "../../shared/fees/feeUI";
import { BackButton } from "../../shared/reports/reportsShared";

const labelSt = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: C.slate,
  marginBottom: "5px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};
const inputSt = {
  width: "100%",
  minHeight: "42px",
  padding: "9px 13px",
  borderRadius: "8px",
  border: `1.5px solid ${C.border}`,
  fontSize: "13px",
  color: C.text,
  background: C.white,
  outline: "none",
  boxSizing: "border-box",
};
const actionBtnSt = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "7px",
  minHeight: "42px",
  padding: "0 16px",
  border: "none",
  borderRadius: "8px",
  background: C.navy,
  color: "#fff",
  fontSize: "13px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};
const ghostBtnSt = {
  ...actionBtnSt,
  background: C.bg,
  color: C.textMid,
  border: `1px solid ${C.border}`,
};
const statBoxSt = {
  padding: "13px 14px",
  border: `1px solid ${C.border}`,
  borderRadius: "8px",
  background: C.white,
  minWidth: 0,
};
const statValueSt = {
  display: "block",
  fontSize: "24px",
  fontWeight: 900,
  lineHeight: 1,
  color: C.navy,
};
const statLabelSt = {
  display: "block",
  marginTop: "7px",
  color: C.slate,
  fontSize: "10px",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const sameIdList = (a = [], b = []) => {
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

const classSectionsOf = (classItem) => {
  const fromList = Array.isArray(classItem?.sections)
    ? classItem.sections
    : [];
  const values = fromList.length
    ? fromList
    : classItem?.section
      ? [classItem.section]
      : [];
  return [
    ...new Set(
      values
        .map((value) => String(value || "").trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
};

const classSessionIdsOf = (classItem) => {
  const fromList = Array.isArray(classItem?.session_ids)
    ? classItem.session_ids
    : [];
  const values = fromList.length
    ? fromList
    : classItem?.session_id
      ? [classItem.session_id]
      : [];
  return [...new Set(values.map((value) => String(value)).filter(Boolean))];
};

function MultiSelectDropdown({
  options,
  selected = [],
  onToggle,
  disabled,
  emptyLabel,
  needsValue,
  placeholder = "Select...",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selectedSet = new Set(selected.map(String));
  const selectedLabels = options
    .filter((option) => selectedSet.has(String(option.value)))
    .map((option) => option.label);

  const summary = selectedLabels.length
    ? selectedLabels.length <= 2
      ? selectedLabels.join(", ")
      : `${selectedLabels.length} selected`
    : placeholder;

  if (!options.length) {
    return (
      <div
        style={{
          ...inputSt,
          minWidth: "210px",
          color: C.slateLight,
          fontStyle: "italic",
        }}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      style={{ position: "relative", minWidth: "210px", maxWidth: "260px" }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...inputSt,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          borderColor: needsValue ? C.amber : open ? C.blue : C.border,
          background: C.white,
          fontWeight: selectedLabels.length ? 600 : 400,
          color: selectedLabels.length ? C.text : C.slateLight,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summary}
        </span>
        <span
          style={{
            color: C.slateLight,
            fontSize: "16px",
            lineHeight: 1,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s ease",
          }}
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 50,
            maxHeight: "220px",
            overflowY: "auto",
            padding: "6px",
            borderRadius: "10px",
            border: `1px solid ${C.border}`,
            background: C.white,
            boxShadow: "0 12px 28px rgba(15,29,54,0.14)",
          }}
        >
          {options.map((option) => {
            const value = String(option.value);
            const checked = selectedSet.has(value);
            return (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => onToggle(value)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 10px",
                  border: "none",
                  borderRadius: "8px",
                  background: checked ? C.blueSoft : "transparent",
                  color: C.text,
                  fontSize: "13px",
                  fontWeight: checked ? 700 : 500,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "4px",
                    border: `1.5px solid ${checked ? C.navy : C.border}`,
                    background: checked ? C.navy : C.white,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 900,
                  }}
                >
                  {checked ? "✓" : ""}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionAssignment({
  campusId,
  campusName,
  onBack,
  showToast,
  onLogout,
}) {
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [campusSections, setCampusSections] = useState([]);
  const [draftAssignments, setDraftAssignments] = useState({});
  const [draftSections, setDraftSections] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [savingClassId, setSavingClassId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [sessionHistory, setSessionHistory] = useState({});
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [pendingSessionClassId, setPendingSessionClassId] = useState(null);
  const [newSession, setNewSession] = useState({
    name: "",
    start_date: "",
    end_date: "",
    is_active: true,
  });

  const getErrorMessage = useCallback(async (res, fallback) => {
    const data = await res.json().catch(() => ({}));
    if (Array.isArray(data.detail)) {
      return data.detail.map((err) => err.msg).join(", ") || fallback;
    }
    return data.detail || fallback;
  }, []);

  const recordSessionHistory = useCallback(
    (
      classId,
      previousSessionId,
      previousSessionName,
      nextSessionId,
      nextSessionName,
    ) => {
      if (
        !classId ||
        !previousSessionId ||
        !nextSessionId ||
        String(previousSessionId) === String(nextSessionId)
      ) {
        return;
      }

      setSessionHistory((prev) => {
        const historyForClass = prev[classId] || [];
        const alreadyTracked = historyForClass.some(
          (entry) =>
            String(entry.previousSessionId) === String(previousSessionId) &&
            String(entry.nextSessionId) === String(nextSessionId),
        );

        if (alreadyTracked) {
          return prev;
        }

        return {
          ...prev,
          [classId]: [
            ...historyForClass,
            {
              id: `${classId}-${previousSessionId}-${nextSessionId}-${Date.now()}`,
              previousSessionId: String(previousSessionId),
              previousSessionName: previousSessionName || "Unknown session",
              nextSessionId: String(nextSessionId),
              nextSessionName: nextSessionName || "Unknown session",
              createdAt: new Date().toISOString(),
            },
          ],
        };
      });
    },
    [],
  );

  const loadData = useCallback(async () => {
    if (!campusId) return;

    setIsLoading(true);
    try {
      const [sessionsRes, classesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/sessions?campus_id=${campusId}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE_URL}/api/classes?campus_id=${campusId}`, {
          headers: getAuthHeaders(),
        }),
      ]);

      if (sessionsRes.status === 401 || classesRes.status === 401) {
        onLogout?.();
        return;
      }
      if (!sessionsRes.ok) {
        throw new Error(
          await getErrorMessage(sessionsRes, "Could not load sessions."),
        );
      }
      if (!classesRes.ok) {
        throw new Error(
          await getErrorMessage(classesRes, "Could not load classes."),
        );
      }

      const [sessionsData, classesData] = await Promise.all([
        sessionsRes.json(),
        classesRes.json(),
      ]);

      const nextSessions = Array.isArray(sessionsData) ? sessionsData : [];
      const nextClasses = Array.isArray(classesData)
        ? classesData.filter(isConfiguredCampusClass)
        : [];

      setSessions(nextSessions);
      setCampusSections(getStoredCampusSections(campusId));
      setClasses(nextClasses);
      setDraftAssignments(
        nextClasses.reduce((acc, classItem) => {
          acc[classItem.id] = classSessionIdsOf(classItem);
          return acc;
        }, {}),
      );
      setDraftSections(
        nextClasses.reduce((acc, classItem) => {
          acc[classItem.id] = classSectionsOf(classItem);
          return acc;
        }, {}),
      );
    } catch (error) {
      console.error("Failed to load session assignment data", error);
      showToast?.(
        "error",
        "Load Error",
        error.message || "Could not load assignment data.",
      );
      setSessions([]);
      setClasses([]);
      setDraftAssignments({});
      setDraftSections({});
    } finally {
      setIsLoading(false);
    }
  }, [campusId, getErrorMessage, onLogout, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAddSessionModal = useCallback((classId = null) => {
    setPendingSessionClassId(classId);
    setNewSession({
      name: "",
      start_date: "",
      end_date: "",
      is_active: true,
    });
    setShowAddSessionModal(true);
  }, []);

  const handleCreateSession = async (e) => {
    if (e) e.preventDefault();

    if (!newSession.name || !newSession.start_date || !newSession.end_date) {
      showToast("error", "Error", "Please fill in all required fields.");
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/campuses/${campusId}/sessions`,
        {
          method: "POST",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify({
            name: newSession.name,
            start_date: newSession.start_date,
            end_date: newSession.end_date,
            is_active: newSession.is_active,
          }),
        },
      );
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(
          await getErrorMessage(res, "Could not create session."),
        );
      }

      const createdSession = await res.json();
      showToast(
        "success",
        "Session created",
        `${newSession.name} has been added.`,
      );
      setShowAddSessionModal(false);
      setPendingSessionClassId(null);
      setNewSession({
        name: "",
        start_date: "",
        end_date: "",
        is_active: true,
      });
      await loadData();

      if (pendingSessionClassId && createdSession?.id) {
        setDraftAssignments((prev) => {
          const current = Array.isArray(prev[pendingSessionClassId])
            ? prev[pendingSessionClassId]
            : [];
          const nextId = String(createdSession.id);
          return {
            ...prev,
            [pendingSessionClassId]: current.includes(nextId)
              ? current
              : [...current, nextId],
          };
        });
      }
    } catch (error) {
      console.error("Failed to create session", error);
      showToast?.(
        "error",
        "Create Error",
        error.message || "Could not create session.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const refreshCampusSections = () => {
      setCampusSections(getStoredCampusSections(campusId));
    };

    refreshCampusSections();
    window.addEventListener("campus-sections-updated", refreshCampusSections);
    window.addEventListener("storage", refreshCampusSections);
    return () => {
      window.removeEventListener(
        "campus-sections-updated",
        refreshCampusSections,
      );
      window.removeEventListener("storage", refreshCampusSections);
    };
  }, [campusId]);

  const sessionNameById = useMemo(() => {
    return new Map(
      sessions.map((session) => [String(session.id), session.name]),
    );
  }, [sessions]);

  const assignedClassCount = classes.filter(
    (classItem) => classSessionIdsOf(classItem).length > 0,
  ).length;
  const sectionedClassCount = classes.filter(
    (classItem) => classSectionsOf(classItem).length > 0,
  ).length;
  const unsectionedClassCount = classes.length - sectionedClassCount;
  const unassignedClassCount = classes.length - assignedClassCount;
  const setupComplete =
    classes.length > 0 &&
    unsectionedClassCount === 0 &&
    unassignedClassCount === 0;
  const draftReadyCount = classes.filter((classItem) => {
    const draftSessions = draftAssignments[classItem.id] || [];
    const draftSecs = draftSections[classItem.id] || [];
    return draftSessions.length > 0 && draftSecs.length > 0;
  }).length;
  const draftProgressRate = classes.length
    ? Math.round((draftReadyCount / classes.length) * 100)
    : 0;

  const sectionOptions = useMemo(() => {
    const existingSections = classes.flatMap((classItem) =>
      classSectionsOf(classItem),
    );
    return [...new Set([...campusSections, ...existingSections])].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [campusSections, classes]);

  const classNameOptions = useMemo(() => {
    return [...new Set(classes.map((classItem) => classItem.name).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [classes]);

  const filteredClasses = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return classes.filter((classItem) => {
      const draftSecs = draftSections[classItem.id] || classSectionsOf(classItem);
      const draftSess = draftAssignments[classItem.id] || classSessionIdsOf(classItem);
      const sessionNames = draftSess
        .map((id) => sessionNameById.get(String(id)))
        .filter(Boolean);

      if (classFilter && String(classItem.name) !== String(classFilter)) return false;
      if (
        sectionFilter &&
        !draftSecs.map(String).includes(String(sectionFilter).toUpperCase())
      ) {
        return false;
      }
      if (sessionFilter && !draftSess.map(String).includes(String(sessionFilter))) {
        return false;
      }
      if (!q) return true;

      return [
        classItem.name,
        ...(draftSecs || []),
        ...sessionNames,
        classItem.description,
        classItem.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [
    classFilter,
    classes,
    draftAssignments,
    draftSections,
    searchTerm,
    sectionFilter,
    sessionFilter,
    sessionNameById,
  ]);

  const dirtyClasses = useMemo(() => {
    return classes.filter((classItem) => {
      const draftSessionValue = draftAssignments[classItem.id] || [];
      const currentSessionValue = classSessionIdsOf(classItem);
      const draftSectionValue = draftSections[classItem.id] || [];
      const currentSectionValue = classSectionsOf(classItem);
      return (
        !sameIdList(draftSessionValue, currentSessionValue) ||
        !sameIdList(draftSectionValue, currentSectionValue)
      );
    });
  }, [classes, draftAssignments, draftSections]);

  const previousSessionsRows = useMemo(() => {
    return Object.entries(sessionHistory).flatMap(([classId, entries]) => {
      const classItem = classes.find(
        (item) => String(item.id) === String(classId),
      );
      return entries.map((entry) => ({
        ...entry,
        classId: String(classId),
        className: classItem?.name || `Class ${classId}`,
      }));
    });
  }, [classes, sessionHistory]);

  const handleSessionDraftChange = (classId, sessionId) => {
    setDraftAssignments((prev) => {
      const current = Array.isArray(prev[classId]) ? prev[classId] : [];
      const value = String(sessionId);
      const next = current.includes(value)
        ? current.filter((id) => id !== value)
        : [...current, value];
      return { ...prev, [classId]: next };
    });
  };

  const handleSectionDraftChange = (classId, section) => {
    setDraftSections((prev) => {
      const current = Array.isArray(prev[classId]) ? prev[classId] : [];
      const value = String(section || "").trim().toUpperCase();
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [classId]: next };
    });
  };

  const saveClassSetup = async (classItem, options = {}) => {
    const selectedSessionIds = draftAssignments[classItem.id] || [];
    const currentSessionIds = classSessionIdsOf(classItem);
    const selectedSections = draftSections[classItem.id] || [];
    const currentSections = classSectionsOf(classItem);

    if (
      sameIdList(selectedSessionIds, currentSessionIds) &&
      sameIdList(selectedSections, currentSections)
    ) {
      if (!options.quiet) {
        showToast?.(
          "success",
          "No Changes",
          "This class setup is already current.",
        );
      }
      return null;
    }

    if (!options.bulk) {
      setSavingClassId(classItem.id);
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/classes/${classItem.id}`, {
        method: "PUT",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify({
          sections: selectedSections,
          session_ids: selectedSessionIds
            .map((id) => parseInt(id, 10))
            .filter((id) => !Number.isNaN(id)),
          section: selectedSections[0] || null,
          session_id: selectedSessionIds[0]
            ? parseInt(selectedSessionIds[0], 10)
            : null,
        }),
      });

      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        throw new Error(
          await getErrorMessage(res, "Could not update class session."),
        );
      }

      const droppedSessions = currentSessionIds.filter(
        (id) => !selectedSessionIds.map(String).includes(String(id)),
      );
      if (droppedSessions.length && selectedSessionIds.length) {
        droppedSessions.forEach((previousId) => {
          recordSessionHistory(
            classItem.id,
            previousId,
            sessionNameById.get(String(previousId)) || "Unknown session",
            selectedSessionIds[0],
            sessionNameById.get(String(selectedSessionIds[0])) || "Unknown session",
          );
        });
      }

      const updatedClass = await res.json();
      setClasses((prev) =>
        prev.map((item) => (item.id === updatedClass.id ? updatedClass : item)),
      );
      setDraftAssignments((prev) => ({
        ...prev,
        [updatedClass.id]: classSessionIdsOf(updatedClass),
      }));
      setDraftSections((prev) => ({
        ...prev,
        [updatedClass.id]: classSectionsOf(updatedClass),
      }));
      if (!options.quiet) {
        showToast?.(
          "success",
          "Class Setup Updated",
          `${updatedClass.name} has been updated.`,
        );
      }
      return updatedClass;
    } catch (error) {
      console.error("Failed to save session assignment", error);
      if (!options.quiet) {
        showToast?.(
          "error",
          "Save Error",
          error.message || "Could not save class setup.",
        );
      }
      throw error;
    } finally {
      if (!options.bulk) {
        setSavingClassId(null);
      }
    }
  };

  const handleSaveAssignment = (classItem) => {
    saveClassSetup(classItem).catch(() => {});
  };

  const handleSaveAll = async () => {
    if (dirtyClasses.length === 0) {
      showToast?.(
        "success",
        "No Changes",
        "All class setup rows are already current.",
      );
      return;
    }

    setIsSavingAll(true);
    setSavingClassId("all");
    try {
      const updatedClasses = [];
      for (const classItem of dirtyClasses) {
        const updatedClass = await saveClassSetup(classItem, {
          bulk: true,
          quiet: true,
        });
        if (updatedClass) updatedClasses.push(updatedClass);
      }
      if (updatedClasses.length) {
        setClasses((prev) =>
          prev.map(
            (item) =>
              updatedClasses.find(
                (updatedClass) => updatedClass.id === item.id,
              ) || item,
          ),
        );
      }
      showToast?.(
        "success",
        "Class Setup Saved",
        `${dirtyClasses.length} class setup rows saved.`,
      );
    } catch (error) {
      showToast?.(
        "error",
        "Save Error",
        error.message || "Could not save all class setup rows.",
      );
    } finally {
      setIsSavingAll(false);
      setSavingClassId(null);
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <section
        style={{
          marginBottom: "16px",
          padding: "18px",
          border: `1px solid ${C.border}`,
          borderRadius: "8px",
          background: C.white,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <BackButton onClick={onBack} label="Back" />
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <span style={{ ...labelSt, color: C.amber, marginBottom: "6px" }}>
              Step 4
            </span>
            <h2
              style={{
                margin: 0,
                color: C.navy,
                fontSize: "26px",
                fontWeight: 900,
                lineHeight: 1.1,
              }}
            >
              Class Setup
            </h2>
            <p
              style={{
                margin: "7px 0 0",
                color: C.slate,
                fontSize: "13px",
                lineHeight: 1.5,
              }}
            >
              Assign one or more created sections and academic sessions to every class
              in {campusName || "this campus"}.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Badge color={setupComplete ? "green" : "amber"}>
              {setupComplete
                ? "Complete"
                : `${classes.length - draftReadyCount} rows need setup`}
            </Badge>
            <button
              type="button"
              style={dirtyClasses.length ? actionBtnSt : ghostBtnSt}
              onClick={handleSaveAll}
              disabled={dirtyClasses.length === 0 || isSavingAll}
            >
              <MdDoneAll />{" "}
              {isSavingAll ? "Saving..." : `Save All (${dirtyClasses.length})`}
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "10px",
            marginTop: "16px",
          }}
        >
          <div style={statBoxSt}>
            <strong style={statValueSt}>{classes.length}</strong>
            <span style={statLabelSt}>Classes</span>
          </div>
          <div style={statBoxSt}>
            <strong style={statValueSt}>{campusSections.length}</strong>
            <span style={statLabelSt}>Sections</span>
          </div>
          <div style={statBoxSt}>
            <strong style={statValueSt}>{sessions.length}</strong>
            <span style={statLabelSt}>Sessions</span>
          </div>
          <div style={statBoxSt}>
            <strong
              style={{
                ...statValueSt,
                color: setupComplete ? C.green : C.amber,
              }}
            >
              {draftProgressRate}%
            </strong>
            <span style={statLabelSt}>Ready</span>
          </div>
        </div>

        <div
          style={{
            marginTop: "16px",
            height: "10px",
            overflow: "hidden",
            borderRadius: "999px",
            background: "#edf2f7",
          }}
          aria-label={`${draftProgressRate}% class setup ready`}
        >
          <span
            style={{
              display: "block",
              width: `${draftProgressRate}%`,
              minWidth: draftProgressRate ? "4px" : 0,
              height: "100%",
              borderRadius: "inherit",
              background: setupComplete ? C.green : C.blue,
            }}
          />
        </div>
      </section>

      <div>
        <Card style={{ borderRadius: "8px" }}>
          <div
            style={{
              padding: "18px 20px",
              borderBottom: `1px solid ${C.border}`,
              display: "grid",
              gap: "14px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    color: C.navy,
                    fontSize: "16px",
                    fontWeight: 900,
                  }}
                >
                  Class Setup
                </h3>
                <p
                  style={{
                    margin: "5px 0 0",
                    color: C.slate,
                    fontSize: "13px",
                  }}
                >
                  Set multiple sections and sessions for each class.
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <Badge color="slate">
                  {filteredClasses.length} of {classes.length}
                </Badge>
                {dirtyClasses.length > 0 && (
                  <Badge color="amber">{dirtyClasses.length} unsaved</Badge>
                )}
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "10px",
              }}
            >
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: C.slateLight,
                    display: "flex",
                    pointerEvents: "none",
                  }}
                >
                  <MdSearch />
                </span>
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search class, section, or session"
                  style={{ ...inputSt, paddingLeft: "36px" }}
                />
              </div>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                aria-label="Filter by class"
                style={inputSt}
              >
                <option value="">All classes</option>
                {classNameOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                aria-label="Filter by section"
                style={inputSt}
              >
                <option value="">All sections</option>
                {sectionOptions.map((section) => (
                  <option key={section} value={section}>
                    Section {section}
                  </option>
                ))}
              </select>
              <select
                value={sessionFilter}
                onChange={(e) => setSessionFilter(e.target.value)}
                aria-label="Filter by session"
                style={inputSt}
              >
                <option value="">All sessions</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "820px",
              }}
            >
              <thead>
                <tr>
                  {["Class", "Section", "Session", "Status", ""].map(
                    (label) => (
                      <th
                        key={label}
                        style={{
                          padding: "11px 16px",
                          textAlign: label ? "left" : "right",
                          color: C.slate,
                          background: C.bg,
                          borderBottom: `1px solid ${C.border}`,
                          fontSize: "11px",
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredClasses.map((classItem) => {
                  const draftSessionValue =
                    draftAssignments[classItem.id] || [];
                  const currentSessionValue = classSessionIdsOf(classItem);
                  const draftSectionValue = draftSections[classItem.id] || [];
                  const currentSectionValue = classSectionsOf(classItem);
                  const hasChanges =
                    !sameIdList(draftSessionValue, currentSessionValue) ||
                    !sameIdList(draftSectionValue, currentSectionValue);
                  const needsSection = draftSectionValue.length === 0;
                  const needsSession = draftSessionValue.length === 0;
                  const isComplete = !needsSection && !needsSession;
                  const statusText = isComplete
                    ? hasChanges
                      ? "Ready to save"
                      : "Ready"
                    : needsSection && needsSession
                      ? "Needs section and session"
                      : needsSection
                        ? "Needs section"
                        : "Needs session";

                  return (
                    <tr
                      key={classItem.id}
                      style={{ background: hasChanges ? "#fffaf0" : C.white }}
                    >
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${C.border}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <strong style={{ display: "block", color: C.text }}>
                          {classItem.name}
                        </strong>
                        <small
                          style={{
                            display: "block",
                            marginTop: "3px",
                            color: C.slateLight,
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          Class ID {classItem.id}
                        </small>
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${C.border}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <MultiSelectDropdown
                          options={sectionOptions.map((section) => ({
                            value: section,
                            label: `Section ${section}`,
                          }))}
                          selected={draftSectionValue}
                          onToggle={(value) =>
                            handleSectionDraftChange(classItem.id, value)
                          }
                          disabled={
                            savingClassId === classItem.id ||
                            savingClassId === "all"
                          }
                          emptyLabel="No sections created yet"
                          needsValue={needsSection}
                          placeholder="Select sections"
                        />
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${C.border}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <MultiSelectDropdown
                          options={sessions.map((session) => ({
                            value: session.id,
                            label: session.name,
                          }))}
                          selected={draftSessionValue}
                          onToggle={(value) =>
                            handleSessionDraftChange(classItem.id, value)
                          }
                          disabled={
                            savingClassId === classItem.id ||
                            savingClassId === "all"
                          }
                          emptyLabel="No sessions created yet"
                          needsValue={needsSession}
                          placeholder="Select sessions"
                        />
                        {/* <button
                          type="button"
                          onClick={() => openAddSessionModal(classItem.id)}
                          style={{
                            marginTop: 8,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            padding: "8px 10px",
                            borderRadius: "8px",
                            border: `1px solid ${C.border}`,
                            background: C.bg,
                            color: C.text,
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          + New session
                        </button> */}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${C.border}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <Badge
                          color={
                            isComplete
                              ? hasChanges
                                ? "amber"
                                : "green"
                              : "amber"
                          }
                        >
                          {statusText}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${C.border}`,
                          textAlign: "right",
                          verticalAlign: "middle",
                        }}
                      >
                        <button
                          type="button"
                          style={hasChanges ? actionBtnSt : ghostBtnSt}
                          onClick={() => handleSaveAssignment(classItem)}
                          disabled={
                            !hasChanges ||
                            savingClassId === classItem.id ||
                            savingClassId === "all"
                          }
                        >
                          {savingClassId === classItem.id ? (
                            "Saving..."
                          ) : (
                            <>
                              <MdSave /> Save
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {isLoading && classes.length === 0 && (
            <div
              style={{
                padding: "70px 20px",
                textAlign: "center",
                color: C.slate,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "15px",
                  color: C.text,
                  marginBottom: "6px",
                }}
              >
                Loading class setup
              </div>
              <p style={{ fontSize: "13px" }}>
                Loading classes and sessions...
              </p>
            </div>
          )}

          {!isLoading && classes.length === 0 && (
            <div
              style={{
                padding: "70px 20px",
                textAlign: "center",
                color: C.slate,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "15px",
                  color: C.text,
                  marginBottom: "6px",
                }}
              >
                No classes yet
              </div>
              <p style={{ fontSize: "13px" }}>
                Create classes first, then manage sections and sessions here.
              </p>
            </div>
          )}

          {!isLoading && classes.length > 0 && filteredClasses.length === 0 && (
            <div
              style={{
                padding: "70px 20px",
                textAlign: "center",
                color: C.slate,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "15px",
                  color: C.text,
                  marginBottom: "6px",
                }}
              >
                No matching classes
              </div>
              <p style={{ fontSize: "13px" }}>
                Try a different class, section, or session search.
              </p>
            </div>
          )}
        </Card>
      </div>

      <Card style={{ marginTop: "16px", borderRadius: "8px" }}>
        <div
          style={{
            padding: "18px 20px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  color: C.navy,
                  fontSize: "16px",
                  fontWeight: 900,
                }}
              >
                Previous Sessions
              </h3>
              <p
                style={{ margin: "5px 0 0", color: C.slate, fontSize: "13px" }}
              >
                Older sessions are moved here when a class is reassigned to a
                new session.
              </p>
            </div>
            <Badge color="slate">{previousSessionsRows.length} moved</Badge>
          </div>
        </div>

        {previousSessionsRows.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "680px",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      padding: "11px 16px",
                      textAlign: "left",
                      color: C.slate,
                      background: C.bg,
                      borderBottom: `1px solid ${C.border}`,
                      fontSize: "11px",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Class
                  </th>
                  <th
                    style={{
                      padding: "11px 16px",
                      textAlign: "left",
                      color: C.slate,
                      background: C.bg,
                      borderBottom: `1px solid ${C.border}`,
                      fontSize: "11px",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Previous session
                  </th>
                  <th
                    style={{
                      padding: "11px 16px",
                      textAlign: "left",
                      color: C.slate,
                      background: C.bg,
                      borderBottom: `1px solid ${C.border}`,
                      fontSize: "11px",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Replaced by
                  </th>
                </tr>
              </thead>
              <tbody>
                {previousSessionsRows.map((entry) => (
                  <tr key={entry.id} style={{ background: C.white }}>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {entry.className}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {entry.previousSessionName}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {entry.nextSessionName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={{
              padding: "70px 20px",
              textAlign: "center",
              color: C.slate,
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: "15px",
                color: C.text,
                marginBottom: "6px",
              }}
            >
              No previous sessions yet
            </div>
            <p style={{ fontSize: "13px" }}>
              Reassign a class to a new session and its old session will appear
              here.
            </p>
          </div>
        )}
      </Card>

      {showAddSessionModal && (
        <div className="modal-overlay">
          <div className="modal-content campus-form-modal">
            <div className="modal-header">
              <h3>Create session for class setup</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => {
                  setShowAddSessionModal(false);
                  setPendingSessionClassId(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateSession}>
                <div className="reg-form-group">
                  <label className="reg-form-label">Session name *</label>
                  <input
                    type="text"
                    className="reg-input"
                    value={newSession.name}
                    onChange={(e) =>
                      setNewSession({ ...newSession, name: e.target.value })
                    }
                    required
                    placeholder="e.g. Academic Year 2026–2027"
                  />
                </div>
                <div className="reg-form-row">
                  <div className="reg-form-group">
                    <label className="reg-form-label">Start date *</label>
                    <input
                      type="date"
                      className="reg-input"
                      value={newSession.start_date}
                      onChange={(e) =>
                        setNewSession({
                          ...newSession,
                          start_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="reg-form-group">
                    <label className="reg-form-label">End date *</label>
                    <input
                      type="date"
                      className="reg-input"
                      value={newSession.end_date}
                      onChange={(e) =>
                        setNewSession({
                          ...newSession,
                          end_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="reg-form-group checkbox-group">
                  <label className="reg-form-label checkbox-label">
                    <input
                      type="checkbox"
                      checked={newSession.is_active}
                      onChange={(e) =>
                        setNewSession({
                          ...newSession,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    Active session
                  </label>
                  <span className="checkbox-helper">
                    Active sessions are highlighted in dashboards and filters.
                  </span>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowAddSessionModal(false);
                      setPendingSessionClassId(null);
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Creating..." : "Create session"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SectionAssignment;
